import { createRef, Component } from 'react';
import * as PropTypes from 'prop-types';
import { MdExpandLess as ArrowIcon } from 'react-icons/md';
import classnames from 'classnames/bind';
import Button from '../Button';
import Connector from '../Connector';
import styles from './Dropdown.module.scss';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const cln = classnames.bind(styles);
// TODO fix d.ts
const compose = (...fns) => (...args) => fns.forEach(fn => fn && fn(...args));

/** This component can be controlled by providing expanded property. If not provided then it handles items visibility through an internal state */
class Dropdown extends Component {
  containerRef = /*#__PURE__*/createRef();
  static propTypes = {
    /** @ignore */
    children: PropTypes.node.isRequired,
    /** Optional className to customize the button */
    className: PropTypes.string,
    /** Render function for a button displayed on the left side of the dropdown button */
    leftButtonRenderer: PropTypes.func,
    /** Render function for a button responsible for expanding dropdown list */
    mainButtonRenderer: PropTypes.func,
    /** Indicates whether list should be visible. If set to true then you're responsible for controlling menu items visibility */
    expanded: PropTypes.bool,
    /** With this prop you cane set initial component state and still leave state handling for this component itself */
    startExpanded: PropTypes.bool,
    /**
     * Whether to auto-hide menu on outside click (works only if this component is not controlled).
     * If expanded property is used then use onOutsideClick handler to be notified when a user clicks outside of the menu list.
     */
    hideOnOutsideClick: PropTypes.bool,
    /** Whether to hide menu on an option click */
    hideOnOptionClick: PropTypes.bool,
    /** Function to be invoked on outside click  */
    onOutsideClick: PropTypes.func,
    /** Function to be invoked on main dropdown button click */
    onClick: PropTypes.func,
    /** Function to be invoked on options menu click */
    onOptionsMenuClick: PropTypes.func,
    /** One of predefined sizes */
    size: PropTypes.oneOf(['big', 'small', 'compact']),
    /** Defines the gap between dropdown button and main button */
    connectGap: PropTypes.number,
    /** Defines the gap between dropdown button and options menu */
    menuGap: PropTypes.number,
    /** Indicates whether to show menu arrow */
    showMenuArrow: PropTypes.bool,
    /** Menu arrow is centered by default. Positive numbers moves the arrow to the right, negative to the left  */
    menuArrowPosition: PropTypes.number,
    /** Allows to set custom class name on the dropdown menu arrow  */
    menuArrowClassName: PropTypes.string,
    /** Optional className to customize the the default button renderer - dropdown arrow */
    buttonRendererClassName: PropTypes.string
  };
  static defaultProps = {
    expanded: undefined,
    startExpanded: false,
    className: undefined,
    hideOnOptionClick: true,
    showMenuArrow: false,
    size: 'big',
    connectGap: 1,
    menuGap: undefined,
    onClick: () => {},
    onOutsideClick: () => {},
    onOptionsMenuClick: () => {},
    hideOnOutsideClick: true,
    leftButtonRenderer: undefined,
    mainButtonRenderer: undefined,
    menuArrowPosition: undefined,
    menuArrowClassName: undefined,
    buttonRendererClassName: undefined
  };
  state = {
    expanded: this.props.startExpanded
  };
  componentDidMount() {
    document.addEventListener('mousedown', this.handleOutsideClick, false);
  }
  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleOutsideClick, false);
  }
  handleOnClick = () => {
    if (!this.isExpandControlled()) {
      this.setState(({
        expanded
      }) => ({
        expanded: !expanded
      }));
    }
  };
  handleOptionsMenuClick = () => {
    const {
      hideOnOptionClick,
      onOptionsMenuClick
    } = this.props;
    if (hideOnOptionClick) {
      this.handleOnClick();
    }
    onOptionsMenuClick();
  };
  handleOutsideClick = event => {
    if (this.containerRef.current.contains(event.target)) {
      return;
    }
    const {
      onOutsideClick
    } = this.props;
    if (!this.isExpandControlled() && this.props.hideOnOutsideClick) {
      this.setState(() => ({
        expanded: false
      }));
    }
    onOutsideClick();
  };
  isExpandControlled = () => this.props.expanded !== undefined;
  mainButtonRenderer = (onClick, isExpanded, rest) => {
    const {
      size,
      buttonRendererClassName
    } = this.props;
    return /*#__PURE__*/_jsx(Button, {
      type: "button",
      selected: isExpanded,
      iconOnly: true,
      size: size,
      className: buttonRendererClassName,
      ...rest,
      onClick: onClick,
      children: /*#__PURE__*/_jsx(ArrowIcon, {
        className: cln('ArrowIcon')
      })
    });
  };
  render() {
    const {
      children,
      className,
      hideOnOutsideClick,
      // eslint-disable-line
      onClick,
      expanded,
      leftButtonRenderer,
      mainButtonRenderer,
      size,
      connectGap,
      menuGap,
      menuArrowPosition,
      menuArrowClassName,
      showMenuArrow,
      startExpanded,
      // eslint-disable-line
      hideOnOptionClick,
      // eslint-disable-line
      onOutsideClick,
      // eslint-disable-line
      onOptionsMenuClick,
      // eslint-disable-line
      buttonRendererClassName,
      // eslint-disable-line
      ...rest
    } = this.props;
    const isExpanded = this.isExpandControlled() ? expanded : this.state.expanded;
    const containerClasses = cln(className, 'Dropdown', `Dropdown--${size}`, {
      'Dropdown--expanded': isExpanded
    });
    const menuStyles = menuGap && {
      top: `calc(100% + ${menuGap}px)`
    };
    const buttonRenderer = mainButtonRenderer || this.mainButtonRenderer;
    return /*#__PURE__*/_jsxs("div", {
      className: containerClasses,
      ref: this.containerRef,
      children: [/*#__PURE__*/_jsxs(Connector, {
        gap: connectGap,
        children: [leftButtonRenderer && leftButtonRenderer(), buttonRenderer(compose(onClick, this.handleOnClick), isExpanded, rest)]
      }), /*#__PURE__*/_jsxs("div", {
        className: cln('MenuItemsList'),
        "data-testid": "dropdown-menu",
        style: menuStyles,
        onClick: this.handleOptionsMenuClick,
        children: [showMenuArrow && /*#__PURE__*/_jsx("div", {
          className: cln('MenuArrow', menuArrowClassName),
          "data-testid": "dropdown-arrow",
          style: {
            left: `${menuArrowPosition || 0}px`
          }
        }), /*#__PURE__*/_jsx("div", {
          className: cln('ItemsContainer'),
          children: children
        })]
      })]
    });
  }
}
function Item({
  children,
  onClick = () => {},
  itemKey,
  className
}) {
  return /*#__PURE__*/_jsx("div", {
    type: "button",
    className: cln('MenuItem', className),
    onClick: () => onClick(itemKey),
    children: children
  });
}
Item.propTypes = {
  /** A key of this item which will be provided as an argument to onClick function */
  itemKey: PropTypes.string.isRequired,
  /** Function to trigger after a button is clicked. */
  onClick: PropTypes.func,
  /** @ignore */
  children: PropTypes.node.isRequired,
  /** Custom class name */
  className: PropTypes.string
};
Dropdown.Item = Item;
export default Dropdown;
export { Item }; // required only for TS declaration files auto-generation